import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Phone, Building2, Clock, Tag, MessageSquare, RefreshCw, ChevronDown, Search, User } from 'lucide-react';
import { Navbar } from '../components/Navbar';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_STYLES = {
  beta: { label: 'Beta Tester', bg: 'bg-[#7ED321]/10', text: 'text-[#3d6910]', border: 'border-[#7ED321]/30' },
  monthly: { label: 'Monthly', bg: 'bg-[#01939E]/10', text: 'text-[#01939E]', border: 'border-[#01939E]/30' },
  annual: { label: 'Annual', bg: 'bg-[#003C59]/10', text: 'text-[#003C59]', border: 'border-[#003C59]/30' },
};

const STATUS_STYLES = {
  new: { label: 'New', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  contacted: { label: 'Contacted', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
  closed: { label: 'Closed', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  declined: { label: 'Declined', bg: 'bg-gray-50', text: 'text-gray-500', border: 'border-gray-200', dot: 'bg-gray-400' },
};

function TierBadge({ tier }) {
  const s = TIER_STYLES[tier] || TIER_STYLES.monthly;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide border ${s.bg} ${s.text} ${s.border}`}>
      {s.label}
    </span>
  );
}

function StatusBadge({ status, onUpdate, leadId }) {
  const [open, setOpen] = useState(false);
  const s = STATUS_STYLES[status] || STATUS_STYLES.new;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${s.bg} ${s.text} ${s.border} hover:opacity-80 transition-opacity`}
        data-testid={`status-badge-${leadId}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
        {s.label}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute z-10 mt-1 right-0 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[120px]">
          {Object.entries(STATUS_STYLES).map(([key, val]) => (
            <button
              key={key}
              onClick={() => { onUpdate(leadId, key); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2 ${status === key ? 'font-semibold' : ''}`}
              data-testid={`status-option-${key}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${val.dot}`} />
              {val.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function LeadsDashboard() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/contact`, { credentials: 'include' });
      const data = await res.json();
      setLeads(data);
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const updateStatus = async (id, newStatus) => {
    try {
      await fetch(`${API}/api/contact/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      setLeads(prev => prev.map(l => l._id === id ? { ...l, status: newStatus } : l));
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const filtered = leads.filter(l => {
    if (filter !== 'all' && l.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (l.name?.toLowerCase().includes(q) || l.email?.toLowerCase().includes(q) || l.business?.toLowerCase().includes(q));
    }
    return true;
  });

  const counts = {
    all: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    closed: leads.filter(l => l.status === 'closed').length,
    declined: leads.filter(l => l.status === 'declined').length,
  };

  return (
    <div className="min-h-screen bg-[#fafbfc]">
      <Navbar />
      {/* Header */}
      <div className="bg-[#003C59] text-white px-6 py-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold" data-testid="leads-title">Leads</h1>
            <p className="text-[#A2D0D5] text-sm mt-0.5">Contact form submissions from the pricing page</p>
          </div>
          <button
            onClick={fetchLeads}
            className="flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition-colors"
            data-testid="refresh-leads-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-5">
          <div className="flex gap-1.5 flex-wrap">
            {Object.entries({ all: 'All', new: 'New', contacted: 'Contacted', closed: 'Closed', declined: 'Declined' }).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  filter === key ? 'bg-[#003C59] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
                data-testid={`filter-${key}`}
              >
                {label} ({counts[key]})
              </button>
            ))}
          </div>
          <div className="relative flex-1 sm:max-w-xs ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search leads..."
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#01939E]/30 focus:border-[#01939E]"
              data-testid="search-leads"
            />
          </div>
        </div>

        {/* Lead cards */}
        {loading && leads.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">Loading leads...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <User className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">{search ? 'No leads match your search' : 'No leads yet'}</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="leads-list">
            {filtered.map(lead => (
              <div
                key={lead._id}
                className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-sm transition-shadow"
                data-testid={`lead-card-${lead._id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-semibold text-[#003C59] text-sm">{lead.name}</h3>
                      <TierBadge tier={lead.tier} />
                      {lead.industry && (
                        <span className="text-[10px] text-gray-400 uppercase tracking-wide flex items-center gap-1">
                          <Tag className="w-3 h-3" />
                          {lead.industry}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500 mt-1.5 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3.5 h-3.5" />
                        <a href={`mailto:${lead.email}`} className="text-[#01939E] hover:underline">{lead.email}</a>
                      </span>
                      {lead.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5" />
                          <a href={`tel:${lead.phone}`} className="hover:underline">{lead.phone}</a>
                        </span>
                      )}
                      {lead.business && (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5" />
                          {lead.business}
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-gray-400">
                        <Clock className="w-3.5 h-3.5" />
                        {timeAgo(lead.created_at)}
                      </span>
                    </div>
                    {lead.message && (
                      <div className="mt-3 flex gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                        <MessageSquare className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                        <p className="leading-relaxed">{lead.message}</p>
                      </div>
                    )}
                  </div>
                  <StatusBadge status={lead.status || 'new'} onUpdate={updateStatus} leadId={lead._id} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
